import { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
  Modal,
} from "react-bootstrap";
import { FaTrash, FaEdit, FaArrowLeft } from "react-icons/fa";

import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import Spinner from "../components/Spinner";
import SuccessModal from "../components/modals/SuccessModal";
import { useNotificationModal } from "../hooks/useNotificationModal";
import "../styles/AddGRN.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function AddGRN() {
  const navigate = useNavigate();
  const { po_number: poParam } = useParams();
  const { userData } = useContext(AuthContext);

  const company_code = userData?.company_code;
  const warehouse_user_id = userData?.id;

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  // State for PO selection
  const [selectedPO, setSelectedPO] = useState(poParam || null);
  const [poDetails, setPODetails] = useState(null);

  // State for GRN creation
  const [grnItems, setGRNItems] = useState([]);
  const [batchNumber, setBatchNumber] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [reference, setReference] = useState("");
  const [grnStatus, setGrnStatus] = useState("");
  // Received date for the GRN header (YYYY-MM-DD for input)
  const [receivedDate, setReceivedDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );

  // State for UI
  const [loading, setLoading] = useState(false);

  // State for GRN header status
  const [headerStatus, setHeaderStatus] = useState("partial");
  const [, setGrnHeaderStatus] = useState("");

  // Add state for locations
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const [rowData, setRowData] = useState({});
  // Selection state for tables
  const [selectedPOItems, setSelectedPOItems] = useState(new Set());
  const [selectAllPO, setSelectAllPO] = useState(false);
  const [, setSelectedGRNItems] = useState(new Set());
  const [, setSelectAllGRN] = useState(false);
  // location selected for batch adding
  const [selectedBatchLocation, setSelectedBatchLocation] = useState("");

  // Column-level filters for PO Items table
  const [poItemsColumnFilter, setPoItemsColumnFilter] = useState({
    material_id: "",
    material_name: "",
    unit_price: "",
    ordered_qty: "",
    max_qty: "",
    total_received: "",
    remaining_qty: "",
    location: "",
  });

  // Column-level filters for Added GRN Items table
  const [grnItemsColumnFilter, setGrnItemsColumnFilter] = useState({
    material_id: "",
    material_name: "",
    ordered_qty: "",
    received_qty: "",
    location: "",
  });

  // Function to check if all items are completed (within tolerance limits)
  const checkAllItemsCompleted = useCallback(() => {
    if (!poDetails || !poDetails.items) return false;

    return poDetails.items.every((item) => {
      const receivedQty = item.total_received || 0;
      const orderedQty = item.ordered_qty;
      const toleranceLimit = item.tolerance_limit || 0;

      // Calculate min and max acceptable quantities
      const minQty = Math.max(
        0,
        orderedQty - (orderedQty * toleranceLimit) / 100,
      );
      const maxQty = orderedQty + (orderedQty * toleranceLimit) / 100;

      // Check if received quantity is within tolerance limits
      return receivedQty >= minQty && receivedQty <= maxQty;
    });
  }, [poDetails]);

  // Handle row data changes
  const handleRowDataChange = (material_id, field, value) => {
    setRowData((prev) => ({
      ...prev,
      [material_id]: {
        ...prev[material_id],
        [field]: value,
      },
    }));
  };

  // Get row data for a specific material
  const getRowData = (material_id, field, defaultValue = "") => {
    return rowData[material_id]?.[field] || defaultValue;
  };

  // Small helper for case-insensitive substring matching
  const matches = (value, filter) => {
    if (!filter) return true;
    const val = value === null || value === undefined ? "" : String(value);
    return val.toLowerCase().includes(filter.toLowerCase());
  };

  // Add item directly from table row
  const handleAddItemFromRow = async (item, overrideLocationId = null) => {
    const received_qty =
      parseInt(getRowData(item.material_id, "received_qty")) || item.remaining_qty;
    const location_id = String(
      overrideLocationId || getRowData(item.material_id, "location_id") || "",
    ).trim();
    const notes = getRowData(item.material_id, "notes", "");

    // Basic validation
    if (!received_qty || received_qty <= 0) {
      showError("Please enter a valid received quantity", "Validation Error");
      return false;
    }
    if (!location_id || location_id === "0") {
      showError("Please select a location", "Validation Error");
      return false;
    }
    if (received_qty > item.remaining_qty) {
      showError(
        `Cannot receive ${received_qty} items. Only ${item.remaining_qty} items remaining`,
        "Validation Error",
      );
      return false;
    }

    try {
      // Add new item (client-side validation already done above)
      const newGRNItem = {
        material_id: item.material_id,
        material_name: item.material_name,
        ordered_qty: item.ordered_qty,
        max_qty: item.max_qty,
        received_qty: received_qty,
        location_id: location_id,
        notes: notes,
        unit_price:
          typeof item.unit_price !== "undefined"
            ? item.unit_price
            : item.unit_price || "",
        tolerance_limit: item.tolerance_limit,
      };

      setGRNItems((prev) => [...prev, newGRNItem]);

      // Update remaining quantity in PO details
      const updatedPODetails = { ...poDetails };
      const itemIndex = updatedPODetails.items.findIndex(
        (poItem) => poItem.material_id === item.material_id,
      );
      if (itemIndex !== -1) {
        updatedPODetails.items[itemIndex].remaining_qty -= received_qty;
        updatedPODetails.items[itemIndex].total_received =
          (updatedPODetails.items[itemIndex].total_received || 0) +
          received_qty;
      }
      setPODetails(updatedPODetails);

      // Clear row data for this item
      setRowData((prev) => {
        const newData = { ...prev };
        delete newData[item.material_id];
        return newData;
      });

      showSuccess(
        `Added ${received_qty} units of Material ${item.material_id} to GRN`,
        "Success",
      );
      return true;
    } catch (err) {
      showError("Error adding GRN item", "Error");
      return false;
    }
  };

  const toggleSelectPOItem = (material_id) => {
    setSelectedPOItems((prev) => {
      // find item to check if receivable
      const item = poDetails?.items?.find((i) => i.material_id === material_id);
      const canReceive =
        item &&
        item.remaining_qty > 0 &&
        poDetails?.header?.status === "Approved" &&
        headerStatus !== "completed" &&
        !loading;
      if (!canReceive) return prev; // don't change selection for disabled rows

      const next = new Set(prev);
      if (next.has(material_id)) next.delete(material_id);
      else {
        next.add(material_id);
        // if a batch location is selected, apply it to the rowData
        if (selectedBatchLocation) {
          handleRowDataChange(material_id, "location_id", selectedBatchLocation);
        }
      }

      // Sync selectAllPO based on visible receivable rows
      try {
        const visibleReceivableMaterials = poDetails.items
          .filter((item) => {
            if (!matches(item.material_id, poItemsColumnFilter.material_id))
              return false;
            if (!matches(item.material_name, poItemsColumnFilter.material_name)) 
                return false;
            if (!matches(item.unit_price, poItemsColumnFilter.unit_price))
              return false;
            if (!matches(item.ordered_qty, poItemsColumnFilter.ordered_qty))
              return false;
            if (!matches(item.max_qty, poItemsColumnFilter.max_qty))
              return false;
            if (
              !matches(
                item.total_received || item.total_received === 0
                  ? item.total_received
                  : "",
                poItemsColumnFilter.total_received,
              )
            )
              return false;
            if (!matches(item.remaining_qty, poItemsColumnFilter.remaining_qty))
              return false;
            const rcv =
              item.remaining_qty > 0 &&
              poDetails.header.status === "Approved" &&
              headerStatus !== "completed" &&
              !loading;
            return rcv;
          })
          .map((i) => i.material_id);

        setSelectAllPO(
          visibleReceivableMaterials.length > 0 &&
            visibleReceivableMaterials.every((s) => next.has(s)),
        );
      } catch (e) {
        // ignore
      }

      return next;
    });
  };

  const handleSelectAllPO = (checked, material_ids = []) => {
    if (checked) {
      setSelectedPOItems(new Set(material_ids));
      setSelectAllPO(true);
      // apply batch location to all selected rows if provided
      if (selectedBatchLocation) {
        setRowData((prev) => {
          const next = { ...prev };
          material_ids.forEach((material_id) => {
            next[material_id] = {
              ...(next[material_id] || {}),
              location_id: selectedBatchLocation,
            };
          });
          return next;
        });
      }
    } else {
      setSelectedPOItems(new Set());
      setSelectAllPO(false);
    }
  };

  // Batch add selected PO items
  const addSelectedItems = async () => {
    if (!poDetails || !poDetails.items) return;
    setLoading(true);
    if (!selectedBatchLocation) {
      showError(
        "Please select a location for selected items",
        "Validation Error",
      );
      setLoading(false);
      return;
    }
    const material_ids = Array.from(selectedPOItems);
    let anySuccess = 0;
    for (const material_id of material_ids) {
      const item = poDetails.items.find((i) => i.material_id === material_id);
      if (!item) continue;
      const ok = await handleAddItemFromRow(item, selectedBatchLocation);
      if (ok) anySuccess++;
      // Small delay to avoid hammering backend if many items
      await new Promise((r) => setTimeout(r, 150));
    }
    setLoading(false);
    if (anySuccess > 0) {
      setSelectedPOItems(new Set());
      setSelectAllPO(false);
      showSuccess(`${anySuccess} item(s) added to GRN`, "Success");
    }
  };

  // GRN selection toggles
  const toggleSelectGRNItem = (key) => {
    setSelectedGRNItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAllGRN = (checked, keys = []) => {
    if (checked) {
      setSelectedGRNItems(new Set(keys));
      setSelectAllGRN(true);
    } else {
      setSelectedGRNItems(new Set());
      setSelectAllGRN(false);
    }
  };

  // Update header status when items change
  useEffect(() => {
    if (checkAllItemsCompleted()) {
      setHeaderStatus("completed");
    } else {
      setHeaderStatus("partial");
    }
  }, [poDetails, checkAllItemsCompleted]);

  // Select PO and fetch details (useCallback to avoid re-creation)
  const handleSelectPO = useCallback(
    async (po_number) => {
      setSelectedPO(po_number);
      setLoading(true);

      try {
        // Fetch raw materials PO details
        const response = await fetch(
          `${BASE_URL}/api/admin/raw-materials/get-raw-materials-po/${po_number}?company_code=${company_code}`,
        );
        const data = await response.json();

        if (data.success) {
          // Initialize items with required fields for GRN
          const tolerance = parseFloat(data.header.tolerance_limit) || 0;
          const itemsWithDefaults = data.items.map((item) => ({
            ...item,
            ordered_qty: item.quantity,
            total_received: 0,
            remaining_qty: item.quantity,
            max_qty: Math.ceil(item.quantity + (item.quantity * tolerance) / 100),
            tolerance_limit: tolerance,
          }));

          const updatedData = { ...data, items: itemsWithDefaults };
          setPODetails(updatedData);

          // Fetch GRN header status for this PO (latest GRN for this PO and company)
          let grnHeaderStatusFetched = "";
          try {
            const grnHeaderResp = await fetch(
              `${BASE_URL}/api/admin/grn/grn-history/${po_number}?company_code=${company_code}&limit=1`,
            );
            const grnHeaderData = await grnHeaderResp.json();
            if (
              grnHeaderData.success &&
              Array.isArray(grnHeaderData.grns) &&
              grnHeaderData.grns.length > 0
            ) {
              grnHeaderStatusFetched = grnHeaderData.grns[0].status || "";
            }
          } catch {}
          setGrnHeaderStatus(grnHeaderStatusFetched);
        } else {
          showError(data.message || "Failed to fetch PO details", "Error");
        }
      } catch (err) {
        showError("Error fetching PO details", "Error");
      } finally {
        setLoading(false);
      }
    },
    [company_code, showError],
  );

  // Load PO details if poParam exists
  useEffect(() => {
    if (poParam) {
      handleSelectPO(poParam);
    }
  }, [poParam, handleSelectPO]);

  // Fetch locations for dropdown
  useEffect(() => {
    if (!company_code) return;
    setLocationsLoading(true);
    fetch(
      `${BASE_URL}/api/admin/locations/get-warehouse-locations?company_code=${company_code}`,
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.locations)) {
          setLocations(data.locations);
        } else {
          setLocations([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching locations:", err); // Debug log
        setLocations([]);
      })
      .finally(() => setLocationsLoading(false));
  }, [company_code, warehouse_user_id]);

  // When batch location changes, apply to selected rows
  useEffect(() => {
    if (!selectedBatchLocation) return;
    setRowData((prev) => {
      const next = { ...prev };
      Array.from(selectedPOItems).forEach((material_id) => {
        next[material_id] = {
          ...(next[material_id] || {}),
          location_id: selectedBatchLocation,
        };
      });
      return next;
    });
  }, [selectedBatchLocation, selectedPOItems]);

  // Delete GRN item
  const handleDeleteGRNItem = (index) => {
    const item = grnItems[index];
    // Restore remaining quantity in PO details
    const updatedPODetails = { ...poDetails };
    const itemIndex = updatedPODetails.items.findIndex(
      (pi) => pi.material_id === item.material_id,
    );
    if (itemIndex !== -1) {
      updatedPODetails.items[itemIndex].remaining_qty += item.received_qty;
      updatedPODetails.items[itemIndex].total_received = Math.max(
        0,
        (updatedPODetails.items[itemIndex].total_received || 0) -
          item.received_qty,
      );
    }
    setPODetails(updatedPODetails);
    // Remove item from grnItems
    setGRNItems((prev) => prev.filter((_, i) => i !== index));
    showSuccess(`Removed Material ${item.material_name} from GRN`, "Success");
  };

  // Submit GRN
  const handleSubmitGRN = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Validation
    if (grnItems.length === 0) {
      showError("Please add at least one item to the GRN", "Validation Error");
      setLoading(false);
      return;
    }
    if (!batchNumber.trim()) {
      showError("Please enter a batch number", "Validation Error");
      setLoading(false);
      return;
    }
    try {
      const payload = {
        po_number: selectedPO,
        grn_items: grnItems.map((item) => ({
          raw_material_id: item.material_id,
          material_name: item.material_name,
          ordered_qty: item.ordered_qty,
          received_qty: item.received_qty,
          location_id: item.location_id,
          notes: item.notes || "",
          unit_price:
            typeof item.unit_price !== "undefined"
              ? item.unit_price
              : item.unit_price || "",
        })),
        warehouse_user_id: warehouse_user_id,
        company_code: company_code,
        // Use the selected date (YYYY-MM-DD) converted to an ISO string
        received_date: new Date(receivedDate).toISOString(),
        batch_number: batchNumber,
        invoice_number: invoiceNumber,
        reference: reference,
        supplier_id: poDetails?.header?.supplier_id,
        status: headerStatus,
      };

      const response = await fetch(
        `${BASE_URL}/api/admin/grn/create-raw-materials-grn`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const data = await response.json();
      if (data.success) {
        showSuccess(
          `GRN created successfully! GRN ID: ${data.grn_id}`,
          "Success",
        );
        setGrnStatus(data.status || "");
        // Reset form
        setGRNItems([]);
        setBatchNumber("");
        setInvoiceNumber("");
        setReference("");
        // Refresh PO details to show updated remaining quantities
        await handleSelectPO(selectedPO);
        setTimeout(() => {
          setGrnStatus("");
        }, 5000);
      } else {
        showError(data.message || "Failed to create GRN", "Error");
      }
    } catch (err) {
      showError("Error creating GRN", "Error");
    } finally {
      setLoading(false);
    }
  };

  // Calculate GRN totals
  const calculateTotals = () => {
    return grnItems.reduce(
      (acc, item) => {
        acc.totalItems += 1;
        acc.totalQty += item.received_qty;
        return acc;
      },
      { totalItems: 0, totalQty: 0 },
    );
  };

  const totals = calculateTotals();

  // Helper to generate batch number using provided date (YYYY-MM-DD) or fallback to today. Format = <companyCode>-DDMMYY<poLast2><invLast2>
  const generateBatchNumber = (invoice, po, dateStr, companyCode) => {
    if (!invoice || !po) return "";
    let dd, mm, yy;
    if (dateStr && typeof dateStr === "string") {
      // Expect dateStr in YYYY-MM-DD
      const parts = dateStr.split("-");
      if (parts.length === 3) {
        yy = String(parts[0]).slice(-2);
        mm = parts[1].padStart(2, "0");
        dd = parts[2].padStart(2, "0");
      }
    }
    if (!dd || !mm || !yy) {
      const now = new Date();
      dd = String(now.getDate()).padStart(2, "0");
      mm = String(now.getMonth() + 1).padStart(2, "0");
      yy = String(now.getFullYear()).slice(-2);
    }
    const poLast2 = String(po).slice(-2).padStart(2, "0");
    const invLast2 = String(invoice).slice(-2).padStart(2, "0");
    return `${dd}${mm}${yy}${poLast2}${invLast2}`;
  };

  // Auto-generate batch number when invoice number changes
  useEffect(() => {
    if (invoiceNumber && selectedPO) {
      setBatchNumber(
        generateBatchNumber(
          invoiceNumber,
          selectedPO,
          receivedDate,
          company_code,
        ),
      );
    } else {
      setBatchNumber("");
    }
  }, [invoiceNumber, selectedPO, receivedDate, company_code]);

  // Note modal state and handlers
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteModalIndex, setNoteModalIndex] = useState(null); // index in grnItems
  const [noteModalValue, setNoteModalValue] = useState("");

  // Open the note modal for a specific GRN item index
  const handleAddNote = (index) => {
    if (typeof index !== "number") return;
    const item = grnItems[index];
    setNoteModalIndex(index);
    setNoteModalValue(item?.notes || "");
    setShowNoteModal(true);
  };

  const handleAddNotes = (materialIdOrIndex, notes) => {
    setGRNItems((prev) => {
      const next = [...prev];
      if (typeof materialIdOrIndex === "number") {
        const idx = materialIdOrIndex;
        if (idx >= 0 && idx < next.length) {
          next[idx] = { ...next[idx], notes };
        }
      } else {
        const material_id = materialIdOrIndex;
        const idx = next.findIndex((i) => i.material_id === material_id);
        if (idx !== -1) next[idx] = { ...next[idx], notes };
      }
      return next;
    });
    showSuccess("Note saved", "Success");
  };

  const handleSaveNote = () => {
    if (noteModalIndex === null) return;
    handleAddNotes(noteModalIndex, noteModalValue);
    setShowNoteModal(false);
    setNoteModalIndex(null);
    setNoteModalValue("");
  };

  return (
    <Container fluid className="add-grn-container">
      {/* Back Button */}
      <Row className="mb-3">
        <Col>
          <Button
            variant="secondary"
            onClick={() => navigate("/warehouse/raw-materials-grn")}
            disabled={loading}
            style={{ padding: "10px" }}
          >
            <FaArrowLeft />
            Back
          </Button>
        </Col>
      </Row>

      {/* Main Content */}
      <Row>
        <Col>
          {/* PO Details and GRN Processing */}
          {poDetails && (
            <>
              <Card className="mb-4 add-grn-card">
                <Card.Header>
                  <Row>
                    <Col>
                      <h5>PO Details: {poDetails.header.po_number}</h5>
                      <div>Supplier: {poDetails.header.supplier_name}</div>
                      <div>Status: {poDetails.header.status}</div>
                      <div>
                        Tolerance Limit: {poDetails.header.tolerance_limit || 0}
                        %
                      </div>
                    </Col>
                    <Col xs="auto">
                      {checkAllItemsCompleted() && (
                        <div className="bg-success text-white fs-6 p-2 rounded">
                          All Items Completed
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body>
                  {/* Show note if PO is not approved */}
                  {poDetails.header.status !== "Approved" && (
                    <Alert variant="warning" className="mb-3">
                      <strong>Note:</strong> This PO is <b>not approved</b>. You
                      cannot add GRN for a pending PO.
                    </Alert>
                  )}

                  {/* PO Items Table */}
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner />
                      <p className="mt-3 text-muted">Loading PO details...</p>
                    </div>
                  ) : (
                    <>
                      <Row className="mb-2 align-items-end">
                        <Col xs="auto">
                          <Form.Group>
                            <Form.Label style={{ fontSize: 12 }}>
                              Location for selected
                            </Form.Label>
                            <Form.Select
                              size="sm"
                              value={selectedBatchLocation}
                              onChange={(e) =>
                                setSelectedBatchLocation(e.target.value)
                              }
                              disabled={locationsLoading}
                              style={{ minWidth: 180 }}
                            >
                              <option value="">Select location</option>
                              {locations.map((location) => (
                                <option
                                  key={location.location_id}
                                  value={location.location_id}
                                >
                                  {location.location_name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs="auto">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={addSelectedItems}
                            disabled={
                              selectedPOItems.size === 0 ||
                              loading ||
                              !selectedBatchLocation ||
                              poDetails.header.status !== "Approved"
                            }
                          >
                            Add selected items
                          </Button>
                        </Col>
                      </Row>

                      <h6>Select items to add to GRN:</h6>
                      <Table striped bordered hover responsive className="mb-4">
                        <thead>
                          <tr>
                            <th style={{ width: "40px" }}>
                              <Form.Check
                                type="checkbox"
                                checked={selectAllPO}
                                onChange={(e) => {
                                  const visibleReceivableMaterials = poDetails.items
                                    .filter((item) => {
                                      if (
                                        !matches(
                                          item.material_id,
                                          poItemsColumnFilter.material_id,
                                        )
                                      )
                                        return false;
                                      if (
                                        !matches(
                                          item.material_name,
                                          poItemsColumnFilter.material_name,
                                        )
                                      )
                                        return false;
                                      if (
                                        !matches(
                                          item.unit_price,
                                          poItemsColumnFilter.unit_price,
                                        )
                                      )
                                        return false;
                                      if (
                                        !matches(
                                          item.ordered_qty,
                                          poItemsColumnFilter.ordered_qty,
                                        )
                                      )
                                        return false;
                                      if (
                                        !matches(
                                          item.max_qty,
                                          poItemsColumnFilter.max_qty,
                                        )
                                      )
                                        return false;
                                      if (
                                        !matches(
                                          item.total_received ||
                                            item.total_received === 0
                                            ? item.total_received
                                            : "",
                                          poItemsColumnFilter.total_received,
                                        )
                                      )
                                        return false;
                                      if (
                                        !matches(
                                          item.remaining_qty,
                                          poItemsColumnFilter.remaining_qty,
                                        )
                                      )
                                        return false;
                                      // only include receivable rows
                                      const rcv =
                                        item.remaining_qty > 0 &&
                                        poDetails.header.status ===
                                          "Approved" &&
                                        headerStatus !== "completed" &&
                                        !loading;
                                      return rcv;
                                    })
                                    .map((i) => i.material_id);
                                  handleSelectAllPO(
                                    e.target.checked,
                                    visibleReceivableMaterials,
                                  );
                                }}
                              />
                            </th>
                            <th className="table-headings">Material ID</th>
                            <th className="table-headings">Material Name</th>
                            <th className="table-headings">Unit Price</th>
                            <th className="table-headings">Ordered Qty</th>
                            <th className="table-headings">
                              Max Qty (with tolerance)
                            </th>
                            <th className="table-headings">Total Received</th>
                            <th className="table-headings">Remaining Qty</th>
                            <th className="table-headings">Location</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Column filters row */}
                          <tr className="table-filters-row">
                            <td></td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Material ID"
                                value={poItemsColumnFilter.material_id}
                                onChange={(e) =>
                                  setPoItemsColumnFilter({
                                    ...poItemsColumnFilter,
                                    material_id: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Material Name"
                                value={poItemsColumnFilter.material_name}
                                onChange={(e) =>
                                  setPoItemsColumnFilter({
                                    ...poItemsColumnFilter,
                                    material_name: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Price"
                                value={poItemsColumnFilter.unit_price}
                                onChange={(e) =>
                                  setPoItemsColumnFilter({
                                    ...poItemsColumnFilter,
                                    unit_price: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Ordered Quantity"
                                value={poItemsColumnFilter.ordered_qty}
                                onChange={(e) =>
                                  setPoItemsColumnFilter({
                                    ...poItemsColumnFilter,
                                    ordered_qty: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Max Quantity"
                                value={poItemsColumnFilter.max_qty}
                                onChange={(e) =>
                                  setPoItemsColumnFilter({
                                    ...poItemsColumnFilter,
                                    max_qty: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Received Quantity"
                                value={poItemsColumnFilter.total_received}
                                onChange={(e) =>
                                  setPoItemsColumnFilter({
                                    ...poItemsColumnFilter,
                                    total_received: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Remaining Quantity"
                                value={poItemsColumnFilter.remaining_qty}
                                onChange={(e) =>
                                  setPoItemsColumnFilter({
                                    ...poItemsColumnFilter,
                                    remaining_qty: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Location"
                                value={poItemsColumnFilter.location}
                                onChange={(e) =>
                                  setPoItemsColumnFilter({
                                    ...poItemsColumnFilter,
                                    location: e.target.value,
                                  })
                                }
                              />
                            </td>
                          </tr>

                          {poDetails.items
                            .filter((item) => {
                              if (
                                !matches(
                                  item.material_id,
                                  poItemsColumnFilter.material_id,
                                )
                              )
                                return false;
                              if (!matches(item.material_name, poItemsColumnFilter.material_name))
                                return false;
                              if (
                                !matches(
                                  item.unit_price,
                                  poItemsColumnFilter.unit_price,
                                )
                              )
                                return false;
                              if (
                                !matches(
                                  item.ordered_qty,
                                  poItemsColumnFilter.ordered_qty,
                                )
                              )
                                return false;
                              if (
                                !matches(
                                  item.max_qty,
                                  poItemsColumnFilter.max_qty,
                                )
                              )
                                return false;
                              if (
                                !matches(
                                  item.total_received ||
                                    item.total_received === 0
                                    ? item.total_received
                                    : "",
                                  poItemsColumnFilter.total_received,
                                )
                              )
                                return false;
                              if (
                                !matches(
                                  item.remaining_qty,
                                  poItemsColumnFilter.remaining_qty,
                                )
                              )
                                return false;
                              return true;
                            })
                            .map((item, idx) => {
                              const receivedQty = item.total_received || 0;
                              const canReceive =
                                item.remaining_qty > 0 &&
                                poDetails.header.status === "Approved" &&
                                headerStatus !== "completed" &&
                                !loading;

                              return (
                                <tr
                                  key={item.material_id}
                                  className={
                                    canReceive
                                      ? "table-row-clickable"
                                      : "table-row-disabled"
                                  }
                                >
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      checked={selectedPOItems.has(item.material_id)}
                                      onChange={() =>
                                        toggleSelectPOItem(item.material_id)
                                      }
                                      disabled={!canReceive}
                                    />
                                  </td>
                                  <td className="table-data">
                                    {item.material_id}
                                  </td>
                                  <td className="table-data">{item.material_name}</td>
                                  <td className="table-data number-col">
                                    {item.unit_price}
                                  </td>
                                  <td className="table-data number-col">
                                    {canReceive ? (
                                      <Form.Control
                                        type="number"
                                        size="sm"
                                        min="1"
                                        max={item.remaining_qty}
                                        value={getRowData(
                                          item.material_id,
                                          "received_qty",
                                          item.remaining_qty,
                                        )}
                                        onChange={(e) =>
                                          handleRowDataChange(
                                            item.material_id,
                                            "received_qty",
                                            e.target.value,
                                          )
                                        }
                                        style={{ width: "80px" }}
                                        placeholder={item.remaining_qty.toString()}
                                      />
                                    ) : (
                                      item.ordered_qty
                                    )}
                                  </td>
                                  <td className="table-data number-col">
                                    {item.max_qty}
                                    {item.tolerance_limit > 0 && (
                                      <small className="text-muted">
                                        {" "}
                                        (+{item.tolerance_limit}%)
                                      </small>
                                    )}
                                  </td>
                                  <td className="table-data number-col">
                                    {receivedQty}
                                  </td>
                                  <td className="table-data number-col">
                                    <div
                                      bg={canReceive ? "success" : "secondary"}
                                    >
                                      {item.remaining_qty}
                                    </div>
                                  </td>
                                  <td className="table-data">
                                    {canReceive
                                      ? // Only show/apply location for rows that are selected.
                                        selectedPOItems.has(item.material_id)
                                        ? (() => {
                                            const rowLocation = getRowData(
                                              item.material_id,
                                              "location_id",
                                              "",
                                            );
                                            const locationId =
                                              rowLocation ||
                                              selectedBatchLocation ||
                                              "";
                                            return locationId
                                              ? locations.find(
                                                  (l) =>
                                                    String(l.location_id) ===
                                                    String(locationId),
                                                )?.location_name || locationId
                                              : "-";
                                          })()
                                        : "-"
                                      : // For disabled/completed rows, display the existing location name if available
                                        locations.find(
                                          (l) =>
                                            String(l.location_id) ===
                                            String(item.location_id),
                                        )?.location_name ||
                                        item.location_name ||
                                        "-"}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </Table>
                    </>
                  )}
                </Card.Body>
              </Card>

              {/* Batch and Invoice Info */}
              <Form className="mb-3">
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Invoice Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        placeholder="Enter invoice number"
                        disabled={
                          poDetails.header.status !== "Approved" || loading
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Date *</Form.Label>
                      <Form.Control
                        type="date"
                        value={receivedDate}
                        onChange={(e) => setReceivedDate(e.target.value)}
                        required
                        disabled={
                          poDetails.header.status !== "Approved" || loading
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Batch Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={batchNumber}
                        onChange={(e) => setBatchNumber(e.target.value)}
                        placeholder="Batch number"
                        required
                        readOnly
                        disabled={loading}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Reference</Form.Label>
                      <Form.Control
                        type="text"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        placeholder="Optional reference"
                        disabled={
                          poDetails.header.status !== "Approved" || loading
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>

              {/* Added GRN Items */}
              {grnItems.length > 0 && (
                <Card className="mb-4">
                  <Card.Header>
                    <h6>
                      Items Added to GRN ({grnItems.length} items,{" "}
                      {totals.totalQty} total quantity)
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Material ID</th>
                          <th>Material Name</th>
                          <th>Ordered Qty</th>
                          <th>Received Qty</th>
                          <th>Location</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Column filters row for added GRN items */}
                        <tr className="table-filters-row">
                          <td>
                            <Form.Control
                              size="sm"
                              type="text"
                              placeholder="Material ID"
                              value={grnItemsColumnFilter.material_id}
                              onChange={(e) =>
                                setGrnItemsColumnFilter({
                                  ...grnItemsColumnFilter,
                                  material_id: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="text"
                              placeholder="Material Name"
                              value={grnItemsColumnFilter.material_name}
                              onChange={(e) =>
                                setGrnItemsColumnFilter({
                                  ...grnItemsColumnFilter,
                                  material_name: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="text"
                              placeholder="Ordered Quantity"
                              value={grnItemsColumnFilter.ordered_qty}
                              onChange={(e) =>
                                setGrnItemsColumnFilter({
                                  ...grnItemsColumnFilter,
                                  ordered_qty: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="text"
                              placeholder="Received Quantity"
                              value={grnItemsColumnFilter.received_qty}
                              onChange={(e) =>
                                setGrnItemsColumnFilter({
                                  ...grnItemsColumnFilter,
                                  received_qty: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="text"
                              placeholder="Location"
                              value={grnItemsColumnFilter.location}
                              onChange={(e) =>
                                setGrnItemsColumnFilter({
                                  ...grnItemsColumnFilter,
                                  location: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td></td>
                        </tr>

                        {grnItems.map((item, index) => {
                          // apply same filtering logic but keep original index so delete targets correct item
                          if (
                            !matches(
                              item.material_id,
                              grnItemsColumnFilter.material_id,
                            )
                          )
                            return null;
                          if (!matches(item.material_name, grnItemsColumnFilter.material_name))
                            return null;
                          if (
                            !matches(
                              item.ordered_qty,
                              grnItemsColumnFilter.ordered_qty,
                            )
                          )
                            return null;
                          if (
                            !matches(
                              item.received_qty,
                              grnItemsColumnFilter.received_qty,
                            )
                          )
                            return null;
                          if (
                            !matches(
                              locations.find(
                                (st) =>
                                  String(st.location_id) ===
                                  String(item.location_id),
                              )?.location_name || "-",
                              grnItemsColumnFilter.location,
                            )
                          )
                            return null;

                          // Use a stable key per row: material_id + optional lot + index
                          const rowKey = `${item.material_id}-${
                            item.lot_no || ""
                          }-${index}`;
                          return (
                            <tr key={rowKey}>
                              <td>{item.material_id}</td>
                              <td>{item.material_name}</td>
                              <td>{item.ordered_qty}</td>
                              <td>
                                <div bg="primary">{item.received_qty}</div>
                              </td>
                              <td>
                                {locations.find(
                                  (st) =>
                                    String(st.location_id) ===
                                    String(item.location_id),
                                )?.location_name || "-"}
                              </td>
                              <td>
                                <FaEdit
                                  className="note-icon"
                                  onClick={() => handleAddNote(index)}
                                  disabled={
                                    poDetails.header.status !== "Approved" ||
                                    loading
                                  }
                                  alt="Add Note"
                                  title="Add Note"
                                ></FaEdit>
                                <FaTrash
                                  className="delete-icon"
                                  onClick={() => handleDeleteGRNItem(index)}
                                  disabled={
                                    poDetails.header.status !== "Approved" ||
                                    loading
                                  }
                                  alt="Delete Item"
                                  title="Delete Item"
                                ></FaTrash>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        {grnStatus && (
                          <div bg="info" className="fs-6">
                            Current Status: {grnStatus}
                          </div>
                        )}
                      </div>
                      <div className="d-flex align-items-center">
                        <Form.Group className="me-3 mb-0">
                          <Form.Label visuallyHidden>Status</Form.Label>
                          <Form.Select
                            value={headerStatus}
                            onChange={(e) => setHeaderStatus(e.target.value)}
                            style={{ minWidth: 140 }}
                            disabled={checkAllItemsCompleted() || loading}
                          >
                            <option value="partial">Partial</option>
                            <option value="completed">Completed</option>
                          </Form.Select>
                          {checkAllItemsCompleted() && (
                            <Form.Text className="text-muted">
                              Auto-set to completed as all items are within
                              tolerance
                            </Form.Text>
                          )}
                        </Form.Group>
                        <Button
                          variant="primary"
                          onClick={handleSubmitGRN}
                          disabled={
                            loading ||
                            grnItems.length === 0 ||
                            !batchNumber.trim() ||
                            poDetails.header.status !== "Approved"
                          }
                        >
                          {loading
                            ? "Submitting GRN..."
                            : `Submit GRN (${grnItems.length} items)`}
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Col>
      </Row>

      {/* Note modal for GRN items */}
      <Modal
        show={showNoteModal}
        onHide={() => setShowNoteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={noteModalValue}
              onChange={(e) => setNoteModalValue(e.target.value)}
              placeholder="Enter note for this item..."
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNoteModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveNote}>
            Save Note
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        title={successTitle}
        message={successMessage}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        title={errorTitle}
        message={errorMessage}
        variant="error"
      />
    </Container>
  );
}
